import { FC, useEffect, useState } from 'react';
import Loader from '../loader/Loader';
import { showToast } from '../../services/toast.service';
import { toastMessages } from '../../constants/errorMessages';
import { LABEL_CONSTANTS, TABLE_HEADER_CONSTANTS } from '../../constants/common';
import { getEmbryoStraws } from '../../api/embryoStrawsApi';
import { EmbryoStraw, Specimen } from '../../types/interfaces';
import AddEmbryoStrawModal from './modal/AddEmbryoStrawModal';
import { TransactionStatusEnum } from '../../types/enums';

type ReconcileEmbryoStrawsProps = {
  specimen: Specimen;
  onChange?: (embryoStraws: EmbryoStraw[]) => void;
  specimenId?: number;
};

const ReconcileEmbryoStraws: FC<ReconcileEmbryoStrawsProps> = ({ specimen, specimenId, onChange }) => {
  const [loading, setLoading] = useState(true);
  const [embryoStraws, setEmbryoStraws] = useState<EmbryoStraw[]>([]);

  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchEmbryoStraws = async () => {
    try {
      const params = {
        include: 'Specimen, Specimen.SpecimenLocations, StorageCanister.StorageTank.StorageSite',
        filter: `specimenId eq ${specimen.specimenId}`,
        excludeStatuses: [TransactionStatusEnum.Pending, TransactionStatusEnum.Hold, TransactionStatusEnum.Processed],
      };
      const { data } = await getEmbryoStraws(params);
      setEmbryoStraws(data);
      onChange?.(data);
    } catch {
      showToast.error(toastMessages.SOMETHING_WENT_WRONG);
    } finally {
      setLoading(false);
    }
  };

  const stripQuantity = (name?: string) => {
    if (!name) return '-';
    return name.replace(/\s*\(\d+\)/, '').trim();
  };

  useEffect(() => {
    if (specimen?.specimenId) fetchEmbryoStraws();
  }, [specimen]);

  const handleAddEmbryoStraws = (rows: any[]) => {
    const updatedRows = [
      ...embryoStraws,
      ...rows.map(row => ({
        ...row,
        embryoStrawCode: null,
        embryoStrawId: null,
      })),
    ];

    setEmbryoStraws(updatedRows);
    onChange?.(updatedRows);
    setIsModalOpen(false);
  };

  const onCountChange = (event: React.ChangeEvent<HTMLInputElement>, index: number) => {
    const value = Number(event.target.value) || 0;

    const updatedStraws = [...embryoStraws];
    updatedStraws[index].embryoCountPerStraw = value;

    setEmbryoStraws(updatedStraws);
    onChange?.(updatedStraws);
  };

  const getTotalEmbryoCount = (): number => embryoStraws.reduce((acc, straw) => acc + (straw.embryoCountPerStraw || 0), 0);

  return (
    <>
      <div className="desk-reconcile-embryo-straws">
        <div className="specimen-table">
          <table>
            <thead>
              <tr>
                <th>{TABLE_HEADER_CONSTANTS.STRAW_LOT}</th>
                <th>{TABLE_HEADER_CONSTANTS.LOCATION}</th>
                <th>{TABLE_HEADER_CONSTANTS.EMBRYO_GRADE}</th>
                <th>{TABLE_HEADER_CONSTANTS.EMBRYO_STAGE}</th>
                <th>{TABLE_HEADER_CONSTANTS.EMBRYO_COUNT}</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr>
                  <td colSpan={5}>
                    <Loader loaderSize="small" />
                  </td>
                </tr>
              )}

              {!loading &&
                embryoStraws?.map((straw, index) => (
                  <tr key={`embryo-straw-${straw.embryoStrawId}-${index}`}>
                    <td>{straw.embryoStrawCode ?? '-'}</td>
                    <td>
                      {straw.storageCanister?.storageTank?.storageSite?.name || '-'} /
                      {straw.storageCanister?.storageTank?.name || '-'} /{stripQuantity(straw.storageCanister?.name)}
                    </td>
                    <td>{straw.embryoGrade?.trim() || '-'}</td>
                    <td>{straw.embryoStage?.trim() || '-'}</td>
                    <td>
                      <input
                        type="text"
                        placeholder="0"
                        value={straw.embryoCountPerStraw}
                        onChange={e => onCountChange(e, index)}
                      />
                    </td>
                  </tr>
                ))}

              {!loading && embryoStraws?.length === 0 && (
                <tr>
                  <td colSpan={5}>{LABEL_CONSTANTS.NO_EMBRYO_STRAWS_FOUND}</td>
                </tr>
              )}

              {!loading && (
                <tr>
                  <td colSpan={4} />
                  <td className="straw-flex">
                    <div className="storage-total-quantity quantity">{getTotalEmbryoCount()}</div>
                    <button type="button" onClick={() => setIsModalOpen(true)} className="add-location-button">
                      +
                    </button>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div className="xs-reconcile-embryo-straws">
        {loading ? (
          <Loader addedSpace loaderSize="small" />
        ) : embryoStraws?.length === 0 ? (
          <div className="result-item">{LABEL_CONSTANTS.NO_EMBRYO_STRAWS_FOUND}</div>
        ) : (
          embryoStraws.map((straw, index) => (
            <div className="card max-width" key={`embryo-straw-${straw.embryoStrawId}-${index}`}>
              <div className="reconcile-canister-specimens">
                <div className="card-content-section">
                  <div className="left center">
                    <label>{TABLE_HEADER_CONSTANTS.LOCATION}:</label>
                    <label>
                      {straw.storageCanister?.storageTank?.storageSite?.name || '-'} /
                      {straw.storageCanister?.storageTank?.name || '-'} /{stripQuantity(straw.storageCanister?.name)}
                    </label>
                  </div>
                  <div className="right column">
                    <label>{TABLE_HEADER_CONSTANTS.STRAW_LOT}:</label>
                    <label>{straw.embryoStrawCode}</label>
                  </div>
                </div>

                <div className="card-content-section">
                  <div className="left center">
                    <label>{`${TABLE_HEADER_CONSTANTS.EMBRYO_GRADE}: ${straw.embryoGrade?.trim() || '-'}`}</label>
                  </div>
                  <div className="left center">
                    <label>{`${TABLE_HEADER_CONSTANTS.EMBRYO_STAGE}: ${straw.embryoStage?.trim() || '-'}`}</label>
                  </div>
                  <div className="right column center">
                    <label>{TABLE_HEADER_CONSTANTS.EMBRYO_COUNT}</label>
                    <input
                      type="text"
                      placeholder="0"
                      value={straw.embryoCountPerStraw}
                      onChange={event => onCountChange(event, index)}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))
        )}

        <div className="location-footer">
          <button type="button" onClick={() => setIsModalOpen(true)} className="add-location-button">
            +
          </button>
          <div className="total-quantity">
            <label>{TABLE_HEADER_CONSTANTS.ACTUAL_QTY}:&nbsp;</label>
            {getTotalEmbryoCount()}
          </div>
        </div>
      </div>

      {isModalOpen && (
        <AddEmbryoStrawModal
          isOpen={isModalOpen}
          setIsOpen={setIsModalOpen}
          onConfirm={handleAddEmbryoStraws}
          specimenId={Number(specimenId)}
        />
      )}
    </>
  );
};

export default ReconcileEmbryoStraws;
